/**
 * KWC · Shop the collection tabs (sections/kwc-launches.liquid)
 * ---------------------------------------------------------------------------
 * Plain deferred script, no dependencies. ARIA tabs: click, or the arrow keys,
 * Home and End on the tab row. Only the first tab's products ship with the
 * page; a tab's products are fetched the first time it is opened, from
 * <collection url>?section_id=kwc-product-tab (sections/kwc-product-tab.liquid,
 * the same card snippet). While that loads the panel keeps the height of the
 * panel it replaces, so the page doesn't jump. If the fetch fails, the panel
 * keeps its "View all" link and the tab is tried again next time.
 * The fetched cards' colour pickers, links and Add to cart are wired up by the
 * card scripts already on the page (kwc-card-variants-lazy.js watches for new
 * cards; product-card and product-form are custom elements).
 */
class KwcShopTabs extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener(
      'click',
      (event) => {
        const tab = /** @type {Element} */ (event.target).closest('[data-kwc-shop-tab]');
        if (tab instanceof HTMLElement) this.#select(tab);
      },
      { signal }
    );

    this.addEventListener(
      'keydown',
      (event) => {
        const tab = /** @type {Element} */ (event.target).closest('[data-kwc-shop-tab]');
        if (!(tab instanceof HTMLElement)) return;
        const tabs = this.#tabs;
        const i = tabs.indexOf(tab);
        let next = -1;
        if (event.key === 'ArrowRight') next = (i + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        if (next < 0) return;
        event.preventDefault();
        this.#select(tabs[next], true);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get #tabs() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-kwc-shop-tab]')]);
  }

  /** @param {HTMLElement} tab */
  #panelFor(tab) {
    const id = tab.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  /**
   * @param {HTMLElement} tab
   * @param {boolean} [focus]
   */
  #select(tab, focus = false) {
    const panel = this.#panelFor(tab);
    if (!panel || tab.getAttribute('aria-selected') === 'true') {
      if (focus) tab.focus();
      return;
    }

    const shown = this.#tabs.map((t) => this.#panelFor(t)).find((p) => p && !p.hidden);
    const height = shown ? shown.offsetHeight : 0;

    for (const t of this.#tabs) {
      const selected = t === tab;
      t.setAttribute('aria-selected', String(selected));
      t.tabIndex = selected ? 0 : -1;
      const p = this.#panelFor(t);
      if (p) p.hidden = !selected;
    }

    if (focus) tab.focus();
    this.#reveal(tab);

    if (panel.dataset.src && !panel.dataset.loaded) this.#load(panel, height);
  }

  /** Scrolls the tab row (not the page) so the chosen tab is in view. @param {HTMLElement} tab */
  #reveal(tab) {
    const row = tab.parentElement;
    if (!row || row.scrollWidth <= row.clientWidth) return;
    const left = tab.offsetLeft - (row.clientWidth - tab.offsetWidth) / 2;
    row.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }

  /**
   * @param {HTMLElement} panel
   * @param {number} height
   */
  async #load(panel, height) {
    panel.dataset.loaded = 'loading';
    panel.style.minBlockSize = `${height}px`;
    panel.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(/** @type {string} */ (panel.dataset.src), window.location.origin);
      url.searchParams.set('section_id', 'kwc-product-tab');
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-kwc-shop-content]');
      if (!content) throw new Error('no tab content');

      const all = content.querySelector('[data-kwc-shop-all]');
      if (all && panel.dataset.label) all.textContent = `View all ${panel.dataset.label}`;

      panel.replaceChildren(document.adoptNode(content));
      panel.dataset.loaded = 'true';
    } catch (error) {
      delete panel.dataset.loaded;
      console.warn('[kwc-shop-tabs] could not load the tab', error);
    } finally {
      panel.removeAttribute('aria-busy');
      panel.style.removeProperty('min-block-size');
    }
  }
}

if (!customElements.get('kwc-shop-tabs')) {
  customElements.define('kwc-shop-tabs', KwcShopTabs);
}
